<template>
  <div
    v-bind="$attrs"
    v-on="$listeners"
    :style="style.container"
  >
    <ModalTenantUserSkillCoedit
      v-if="!isViewingOwnPage"
      @coedit-skills="coeditTenantUserSkills"
    />
    <ModalTenantUserSkillEditPrimary
      v-if="isViewingOwnPage"
      @create-skill="createTenantUserSkill"
      @delete-skill="deleteTenantUserSkill"
      @update-skill="updateTenantUserSkill"
    />
    <DialogRevokeCoeditPrimary @revoke-coedit="revokeTenantUserCoedit" />
    <UserHeader
      :isMyself="isViewingOwnPage"
      :tenantUser="tenantUser"
    />
    <div :style="getPlaceholderStyle" />
    <div :style="style.innerContainer">
      <HeaderXlargePrimary>
        {{ firstName }} {{ lastName }}
      </HeaderXlargePrimary>
      <div :style="style.titleContainer">
        <TextMediumSecondary
          v-if="title.length > 0"
          :truncate="true"
        >
          {{ title }}
        </TextMediumSecondary>
        <TextMutedSecondary v-else>
          Title not yet set
        </TextMutedSecondary>
        <TextButtonPrimary
          v-if="isViewingOwnPage"
          @click="openModalToEditTenantUserProfile"
        >
          Edit profile
        </TextButtonPrimary>
      </div>
      <div :style="style.iconExpandContainer">
        <GoogleMaterialIconPrimary
          :content="iconExpandContent"
          :color="styleVariable.color.icon.expand"
          @click="() => { showUserProfile = !showUserProfile }"
        />
        <TextButtonMuted
          @click="() => { showUserProfile = !showUserProfile }"
          :style="style.textButtonExpand"
        >
          {{ textExpand }}
        </TextButtonMuted>
      </div>
      <UserProfile
        v-if="showUserProfile"
        :tenantUser="tenantUser"
      />
      <div :style="style.skillsContainer">
        <TabPrimary
          v-model="tabIndex"
          v-if="loadedTenantUserSkills"
          :tabs="tabs"
          :style="style.tab"
        />
        <CardPrimary :style="style.card">
          <SkillsGraphWrapperPrimary
            :isViewingOwnPage="isViewingOwnPage"
            :loadedTenantUserSkills="loadedTenantUserSkills"
            :tenantUser="tenantUser"
            :tenantUserSkills="tenantUserSkills"
            :triggerFitView="triggerFitView"
            @edit-skill="openModalToEditTenantUserSkill"
            :style="getSkillsGraphStyle"
          >
            <template v-slot:footer>
              <template v-if="isViewingOwnPage">
                <div :style="style.skillsGraphFooterText">
                  Click skill to edit.
                </div>
                <ButtonPrimary
                  @click="openModalToAddTenantUserSkill"
                  :style="style.skillsGraphFooterButton"
                >
                  Add skill
                </ButtonPrimary>
              </template>
              <template v-else>
                <div />
                <ButtonPrimary
                  @click="openModalToCoeditTenantUserSkills"
                  :style="style.skillsGraphFooterButton"
                >
                  Tag skills
                </ButtonPrimary>
              </template>
            </template>
          </SkillsGraphWrapperPrimary>
          <CoeditHistory
            :isViewingOwnPage="isViewingOwnPage"
            :tenantUserSkills="coeditedTenantUserSkills"
            :visitor="visitor"
            :style="getCoeditHistoryStyle"
            @review-skill="openModalToEditTenantUserSkill"
            @revoke-skill="openDialogToRevokeTenantUserCoedit"
          />
        </CardPrimary>
      </div>
    </div>
  </div>
</template>

<script>
  import ApiTenantUser from '@js/api/tenant-user'
  import ApiTenantUserLink from '@js/api/tenant-user-link'
  import ApiTenantUserCoedit from '@js/api/tenant-user-coedit'
  import ApiTenantUserSkill from '@js/api/tenant-user-skill'
  import ButtonPrimary from '@modules/v2/button/button-primary'
  import CardPrimary from '@modules/v2/card/card-primary'
  import CoeditHistory from '@users/modules/coedit-history'
  import DialogRevokeCoeditPrimary from '@dialogs/dialog-revoke-coedit-primary'
  import GoogleMaterialIconPrimary
    from '@modules/v2/icon/google-material-icon-primary'
  import HeaderXlargePrimary from '@modules/v2/header/header-xlarge-primary'
  import HttpStatus from 'http-status-codes'
  import ModalTenantUserSkillCoedit
    from '@users/modals/modal-tenant-user-skill-coedit'
  import ModalTenantUserSkillEditPrimary
    from '@modals/modal-tenant-user-skill-edit-primary'
  import SkillsGraphWrapperPrimary
    from '@modules/v2/skills/skills-graph-wrapper-primary'
  import TabPrimary from '@modules/v2/tab/tab-primary'
  import TextButtonMuted from '@modules/v2/button/text-button-muted'
  import TextButtonPrimary from '@modules/v2/button/text-button-primary'
  import TextMediumSecondary from '@modules/v2/text/text-medium-secondary'
  import TextMutedSecondary from '@modules/v2/text/text-muted-secondary'
  import UserHeader from '@users/modules/user-header'
  import UserProfile from '@users/modules/user-profile'
  import { DIALOG_NAMES, MODAL_WINDOW_NAMES, Z_INDEXES } from '@js/constants'
  import { FormTenantUserSkill } from '@js/forms/tenant-user-skill'
  import { getCoeditedTenantUserSkills } from '@js/skill-utils'
  import { Layout } from '@js/styles'
  import { mapGetters } from 'vuex'

  export default {
    name: 'UserView',
    inheritAttrs: false,
    components: {
      ButtonPrimary,
      CardPrimary,
      CoeditHistory,
      DialogRevokeCoeditPrimary,
      GoogleMaterialIconPrimary,
      HeaderXlargePrimary,
      ModalTenantUserSkillCoedit,
      ModalTenantUserSkillEditPrimary,
      SkillsGraphWrapperPrimary,
      TabPrimary,
      TextButtonMuted,
      TextButtonPrimary,
      TextMediumSecondary,
      TextMutedSecondary,
      UserHeader,
      UserProfile,
    },
    props: {
      isViewingOwnPage: {
        type: Boolean,
        required: false,
        default: false,
      },
      tenantUser: {
        type: Object,
        required: false,
        default: () => {},
      },
      visitor: {
        type: Object,
        required: false,
        default: () => {},
      },
    },
    data: function () {
      return {
        loadedTenantUserSkills: false,
        showUserProfile: false,
        skillsGraphWidth: 400,
        tabIndex: 0,
        tenantUserSkills: [],
        triggerFitView: false,
        styleVariable: {
          color: {
            icon: {
              expand: 'none',
            },
          },
        },
        style: {
          card: {
            height: '400px',
            margin: `${Layout.space.md} 0`,
            width: '100%',
          },
          container: {
            width: '100%',
            margin: `0 0 ${Layout.space.xl} 0`,
          },
          iconExpandContainer: {
            alignItems: 'center',
            display: 'flex',
            margin: `${Layout.space.md} 0 12px`,
          },
          innerContainer: {
            margin: `${Layout.space.lg} auto`,
            maxWidth: Layout.container.md.maxWidth,
            padding: Layout.container.md.padding,
            width: '100%',
          },
          skillsContainer: {
            margin: '32px 0 0 0',
            width: '100%',
          },
          skillsGraphFooterButton: {
            zIndex: Z_INDEXES.USER.SKILL_GRAPH + 1,
          },
          skillsGraphFooterText: {},
          textButtonExpand: {
            margin: `0 0 0 ${Layout.space.xs}`,
          },
          titleContainer: {
            alignItems: 'center',
            display: 'flex',
            justifyContent: 'space-between',
            margin: `${Layout.space.sm} 0 0 0`,
            width: '100%',
          },
          userProfile: {
          },
        },
      };
    },
    created: function () {
      this.styleVariable.color.icon.expand = this.color.dark3();
      this.style.skillsGraphFooterText.color = this.color.dark3();
    },
    mounted: async function () {
      document.title = (
        `${this.tenantUser?.user?.firstName} ${this.tenantUser?.user?.lastName}`
      );
      await this.fetchTenantUserSkills();
    },
    computed: {
      ...mapGetters('colorTheme', ['color']),
      coeditedTenantUserSkills: function () {
        return getCoeditedTenantUserSkills(this.visitor.id, this.tenantUserSkills);
      },
      firstName: function () {
        return this.tenantUser?.user?.firstName;
      },
      getCoeditHistoryStyle: function () {
        return {
          borderRadius: `0 0 ${Layout.space.sm} ${Layout.space.sm}`,
          display: this.tabIndex === 1 ? 'block' : 'none',
          height: '100%',
          padding: `0 ${Layout.space.md} 0 ${Layout.space.md}`,
          width: '100%',
        };
      },
      getPlaceholderStyle: function () {
        return {
          height: this.isViewingOwnPage ? '40px' : '20px',
        };
      },
      getSkillsGraphStyle: function () {
        return {
          borderRadius: `0 0 ${Layout.space.sm} ${Layout.space.sm}`,
          display: this.tabIndex === 0 ? 'block' : 'none',
          height: '100%',
          width: '100%',
        };
      },
      iconExpandContent: function () {
        return this.showUserProfile ? 'expand_less' : 'expand_more';
      },
      lastName: function () {
        return this.tenantUser?.user?.lastName;
      },
      tabs: function () {
        return [
          {
            label: 'Graph',
            disabled: false,
          },
          {
            label: 'Tag requests',
            disabled: this.coeditedTenantUserSkills.length === 0,
          },
        ];
      },
      textExpand: function () {
        return this.showUserProfile ? 'Hide details' : 'See full profile';
      },
      title: function () {
        return this.tenantUser?.title ? this.tenantUser.title : '';
      },
    },
    methods: {
      coeditTenantUserSkills: async function (tags) {
        if (tags.length === 0) {
          return;
        }

        try {
          this.loadedTenantUserSkills = await false;
          const payloads = await tags
            .reduce((result, elem) => {
              const form = new FormTenantUserSkill();
              form.data.skillName = elem;
              result.push(form.getPostPayload());
              return result;
            }, []);
          const api = await new ApiTenantUserSkill(this.tenantUser.id);
          const res = await api.post(payloads);
          await this.tenantUserSkills.push(...res.data);
          this.$store.commit(
            'notification/setMessage',
            tags.length === 1
              ? `Added ${tags[0]}`
              : `Added ${tags.length} skills`,
          );
        } catch (error) {
          console.error(error);
          if (error?.response?.status === HttpStatus.CONFLICT) {
            this.$store.commit(
              'notification/setWarning',
              'The skill is already registered'
            );
          } else {
            this.$store.commit(
              'notification/setWarning',
              'Failed to add skill',
            );
          }
        } finally {
          this.loadedTenantUserSkills = true;
          this.$modal.hide(MODAL_WINDOW_NAMES.CREATE_OTHER_TENANT_USER_SKILL_PRIMARY);
        }
      },
      createTenantUserSkill: async function (form) {
        try {
          this.loadedTenantUserSkills = await false;
          const payload = await form.getPostPayload();
          const api = await new ApiTenantUserSkill(this.tenantUser.id);
          const res = await api.post(payload);
          await this.tenantUserSkills.push(res.data);
        } catch (error) {
          console.error(error);
          if (error?.response?.status === HttpStatus.CONFLICT) {
            this.$store.commit(
              'notification/setWarning',
              'The skill is already registered'
            );
          } else {
            this.$store.commit(
              'notification/setWarning',
              'Failed to add skill',
            );
          }
        } finally {
          this.hideModalWindowToEditTenantUserSkill();
          this.loadedTenantUserSkills = true;
        }
      },
      deleteTenantUserSkill: async function (form) {
        try {
          this.loadedTenantUserSkills = await false;
          const tenantUserSkillId = await form.params.id;
          const api = await new ApiTenantUserSkill(
              this.tenantUser.id, tenantUserSkillId);
          const res = await api.delete();
          await this.removeTenantUserSkill(res.data.id);
        } catch (error) {
          console.error(error);
          this.$store.commit(
            'notification/setWarning',
            'Failed to delete skill',
          );
        } finally {
          this.hideModalWindowToEditTenantUserSkill();
          this.loadedTenantUserSkills = true;
        }
      },
      fetchTenantUserSkills: async function () {
        try {
          this.loadedTenantUserSkills = await false;
          const api = await new ApiTenantUserSkill(this.tenantUser.id);
          const res = await api.get();
          this.tenantUserSkills = await res.data;
          this.loadedTenantUserSkills = await true;
        } catch (error) {
          this.loadedTenantUserSkills = false;
          console.error(error);
          this.$store.commit('notification/setWarning', 'Failed to load skill data.');
          this.$router.push({ name: 'private-home' });
        }
      },
      hideModalWindowToEditTenantUserSkill: function () {
        this.$modal.hide(MODAL_WINDOW_NAMES.CONFIGURE_TENANT_USER_SKILL_PRIMARY);
      },
      openDialogToRevokeTenantUserCoedit: function (tenantUserSkill) {
        const params = {
          tenantUserSkill: tenantUserSkill,
        };
        this.$modal.show(DIALOG_NAMES.REVOKE_TENANT_USER_COEDIT_PRIMARY, params);
      },
      openModalToAddTenantUserSkill: function () {
        if (this.isViewingOwnPage) {
          const params = {
            tenantUserSkill: null,
            tenantUserSkills: this.tenantUserSkills,
          };
          this.$modal.show(MODAL_WINDOW_NAMES.CONFIGURE_TENANT_USER_SKILL_PRIMARY, params);
        }
      },
      openModalToCoeditTenantUserSkills: function () {
        const params = {
          tenantUser: this.tenantUser,
          tenantUserSkills: this.tenantUserSkills,
        };
        this.$modal.show(MODAL_WINDOW_NAMES.CREATE_OTHER_TENANT_USER_SKILL_PRIMARY, params);
      },
      openModalToEditTenantUserProfile: function () {
        const params = {
          tenantUser: this.tenantUser,
        };
        this.$modal.show(MODAL_WINDOW_NAMES.UPDATE_TENANT_USER_PRIMARY, params);
      },
      openModalToEditTenantUserSkill: function (tenantUserSkill) {
        if (this.isViewingOwnPage) {
          const params = {
            tenantUserSkill: tenantUserSkill,
            tenantUserSkills: this.tenantUserSkills,
          };
          this.$modal.show(MODAL_WINDOW_NAMES.CONFIGURE_TENANT_USER_SKILL_PRIMARY, params);
        }
      },
      removeTenantUserCoedit: function (tenantUserSkillId) {
        const index = this.tenantUserSkills.findIndex(
          elem => elem.id === tenantUserSkillId);
        if (index >= 0) {
          this.tenantUserSkills.splice(index, 1);
        }
      },
      removeTenantUserSkill: function (tenantUserSkillId) {
        const index = this.tenantUserSkills.findIndex(
          elem => elem.id === tenantUserSkillId);
        if (index >= 0) {
          this.tenantUserSkills.splice(index, 1);
        }
      },
      revokeTenantUserCoedit: async function (tenantUserSkill) {
        try {
          const api = await new ApiTenantUserSkill(
            tenantUserSkill.tenantUser.id, tenantUserSkill.id);
          const res = await api.delete();
          await this.removeTenantUserCoedit(res.data.id);
          await this.$store.commit(
            'notification/setMessage',
            `Revoked ${tenantUserSkill.skill.name}`,
          );
        } catch (error) {
          console.error(error);
          this.$store.commit(
            'notification/setWarning',
            'Failed to revoke skill',
          );
        } finally {
          this.$modal.hide(DIALOG_NAMES.REVOKE_TENANT_USER_COEDIT_PRIMARY);
        }
      },
      updateLocalTenantUserSkills: function (tenantUserSkill) {
        const sourceId = tenantUserSkill.id;

        this.tenantUserSkills = this.tenantUserSkills
          .reduce((result, elem) => {
            if (elem.id === sourceId) {
              elem = tenantUserSkill;
            } else {
              const deletedFromTargets = (
                elem.targets.includes(sourceId) &&
                !tenantUserSkill.targets.includes(elem.id)
              );
              const addedToTargets = (
                !elem.targets.includes(sourceId) &&
                tenantUserSkill.targets.includes(elem.id)
              );
              if (deletedFromTargets) {
                const index = elem.targets.findIndex(elem => elem === sourceId);
                elem.targets.splice(index, 1);
              } else if (addedToTargets) {
                elem.targets.push(sourceId);
              }
            }

            result.push(elem);
            return result;
          }, []
        );
      },
      updateTenantUserSkill: async function (form) {
        try {
          this.loadedTenantUserSkills = await false;
          const tenantUserSkillId = form.params.id;
          const payload = form.getPutPayload();
          const api = new ApiTenantUserSkill(
            this.tenantUser.id,
            tenantUserSkillId,
          );
          const res = await api.put(payload);
          this.updateLocalTenantUserSkills(res.data);
        } catch (error) {
          console.error(error);
          this.$store.commit(
            'notification/setWarning',
            'Failed to update skill',
          );
        } finally {
          this.hideModalWindowToEditTenantUserSkill();
          this.tabIndex = 0;
          this.loadedTenantUserSkills = true;
        }
      },
    },
    watch: {
      tabIndex: function (newVal, oldVal) {
        if (newVal === 0) {
          this.triggerFitView = !this.triggerFitView;
        }
      },
    },
  };
</script>
